import { Header } from "@/components/Header";
import { Footer } from "@/components/Footer";
import { SEO } from "@/components/SEO";

const TermsOfUse = () => {
  return (
    <div className="min-h-screen bg-background">
      <SEO 
        title="Terms of Use"
        description="CSW Terms of Use. Read our terms and conditions for using virtual study rooms, productivity tools, and community features."
        keywords="csw terms of use, study room terms, user agreement"
        canonicalUrl="/terms-of-use/"
      />
      <Header />
      
      {/* Hero Section */}
      <section className="relative pt-32 pb-16 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-primary/5 via-background to-secondary/5" />
        <div className="absolute top-20 left-1/4 w-96 h-96 bg-primary/10 rounded-full blur-3xl" />
        <div className="absolute bottom-20 right-1/4 w-96 h-96 bg-secondary/10 rounded-full blur-3xl" />
        
        <div className="container mx-auto px-4 relative z-10">
          <div className="max-w-4xl mx-auto text-center">
            <h1 className="text-4xl md:text-5xl font-display font-bold mb-6">
              <span className="bg-gradient-to-r from-primary via-secondary to-accent bg-clip-text text-transparent">
                Terms of Use
              </span>
            </h1>
            <p className="text-lg text-muted-foreground">
              CSW – Chill Study Work App
            </p>
          </div>
        </div>
      </section>

      {/* Content Section */}
      <section className="py-16">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto bg-card/50 backdrop-blur-sm border border-border rounded-2xl p-8 md:p-12">
            <div className="prose prose-lg dark:prose-invert max-w-none">
              
              <div className="mb-12">
                <h2 className="text-2xl font-display font-bold text-foreground mb-4">1. Introduction</h2>
                <p className="text-muted-foreground leading-relaxed">
                  Welcome to CSW – Chill Study Work App ("CSW," "we," "our," or "us"). These Terms of Use ("Terms") govern your access to and use of our app and services. By using CSW, you agree to comply with and be bound by these Terms. If you do not agree with these Terms, please do not use our app.
                </p>
              </div>

              <div className="mb-12">
                <h2 className="text-2xl font-display font-bold text-foreground mb-4">2. Eligibility</h2>
                <div className="space-y-4 text-muted-foreground leading-relaxed">
                  <p><strong className="text-foreground">Age Requirement:</strong> You must be at least 13 years old to use CSW. If you are under the age of 16, you must have the permission of a parent or legal guardian to use the app.</p>
                  <p><strong className="text-foreground">Compliance with Laws:</strong> You must comply with all applicable local, national, and international laws and regulations when using our app.</p>
                </div>
              </div>

              <div className="mb-12">
                <h2 className="text-2xl font-display font-bold text-foreground mb-4">3. Account Registration and Security</h2>
                <div className="space-y-4 text-muted-foreground leading-relaxed">
                  <p><strong className="text-foreground">Registration:</strong> To access certain features of CSW, you must create an account by providing accurate and complete information. You are responsible for maintaining the accuracy of your account information.</p>
                  <p><strong className="text-foreground">Account Security:</strong> You are responsible for safeguarding your account credentials. You agree to notify us immediately of any unauthorized use of your account. We are not liable for any loss or damage arising from your failure to protect your account.</p>
                  <p><strong className="text-foreground">Email Verification:</strong> Upon registration, you will receive a verification email. Your account must be verified before you can fully access our services.</p>
                </div>
              </div>

              <div className="mb-12">
                <h2 className="text-2xl font-display font-bold text-foreground mb-4">4. Use of the App</h2>
                <div className="space-y-4 text-muted-foreground leading-relaxed">
                  <p><strong className="text-foreground">Personal Use:</strong> CSW is intended for personal, non-commercial use only. You may not use the app for any commercial purpose without our express consent.</p>
                  <p><strong className="text-foreground">Prohibited Activities:</strong> You agree not to engage in any of the following activities:</p>
                  <ul className="list-disc list-inside space-y-2 ml-4">
                    <li>Posting or transmitting content that is unlawful, harmful, threatening, abusive, harassing, defamatory, vulgar, obscene, or otherwise objectionable.</li>
                    <li>Impersonating any person or entity or falsely stating or otherwise misrepresenting your affiliation with a person or entity.</li>
                    <li>Engaging in or promoting illegal activities.</li>
                    <li>Accessing or using the app in any way that could harm or disrupt the app, servers, or networks connected to the app.</li>
                    <li>Using automated systems, bots, or scripts to access or interact with the app.</li>
                  </ul>
                </div>
              </div>

              <div className="mb-12">
                <h2 className="text-2xl font-display font-bold text-foreground mb-4">5. Content Monitoring and Enforcement</h2>
                <div className="space-y-4 text-muted-foreground leading-relaxed">
                  <p><strong className="text-foreground">Content Standards:</strong> You are solely responsible for the content you post or share on CSW. All content must comply with our content guidelines, which prohibit hate speech, harassment, violence, sexual content, illegal activities, and other inappropriate content.</p>
                  <p><strong className="text-foreground">Monitoring:</strong> We reserve the right to monitor, review, and remove any content that violates these Terms or our content guidelines. We may suspend or terminate your account if you violate these Terms.</p>
                </div>
              </div>

              <div className="mb-12">
                <h2 className="text-2xl font-display font-bold text-foreground mb-4">6. Intellectual Property</h2>
                <div className="space-y-4 text-muted-foreground leading-relaxed">
                  <p><strong className="text-foreground">Ownership:</strong> All content and materials available on CSW, including but not limited to text, graphics, logos, images, and software, are the property of CSW or its licensors and are protected by intellectual property laws.</p>
                  <p><strong className="text-foreground">License:</strong> We grant you a limited, non-exclusive, non-transferable license to access and use the app for your personal use. You may not reproduce, distribute, modify, or create derivative works from any content on the app without our prior written consent.</p>
                </div>
              </div>

              <div className="mb-12">
                <h2 className="text-2xl font-display font-bold text-foreground mb-4">7. Microtransactions and Payments</h2>
                <div className="space-y-4 text-muted-foreground leading-relaxed">
                  <p><strong className="text-foreground">In-App Purchases:</strong> CSW may offer in-app purchases for virtual goods or services. All purchases are final and non-refundable, except as required by law.</p>
                  <p><strong className="text-foreground">Payment Information:</strong> If you make a purchase, you must provide accurate payment information. You authorize us to charge the applicable fees to your designated payment method.</p>
                </div>
              </div>

              <div className="mb-12">
                <h2 className="text-2xl font-display font-bold text-foreground mb-4">8. Termination</h2>
                <div className="space-y-4 text-muted-foreground leading-relaxed">
                  <p><strong className="text-foreground">Termination by You:</strong> You may terminate your account at any time by contacting us at info@csw.live.</p>
                  <p><strong className="text-foreground">Termination by Us:</strong> We reserve the right to suspend or terminate your account, or restrict your access to the app, at any time and for any reason, including if you violate these Terms.</p>
                  <p><strong className="text-foreground">Effect of Termination:</strong> Upon termination, your right to use the app will immediately cease. All provisions of these Terms that by their nature should survive termination shall survive, including ownership provisions, warranty disclaimers, indemnity, and limitations of liability.</p>
                </div>
              </div>

              <div className="mb-12">
                <h2 className="text-2xl font-display font-bold text-foreground mb-4">9. Disclaimer of Warranties</h2>
                <p className="text-muted-foreground leading-relaxed">
                  CSW is provided "as is" and "as available" without warranties of any kind, either express or implied. We do not warrant that the app will be uninterrupted, secure, or error-free, or that any defects will be corrected. We expressly disclaim any warranties of merchantability, fitness for a particular purpose, and non-infringement.
                </p>
              </div>

              <div className="mb-12">
                <h2 className="text-2xl font-display font-bold text-foreground mb-4">10. Limitation of Liability</h2>
                <p className="text-muted-foreground leading-relaxed">
                  To the fullest extent permitted by law, CSW shall not be liable for any indirect, incidental, special, consequential, or punitive damages, or any loss of profits or revenues, whether incurred directly or indirectly, or any loss of data, use, goodwill, or other intangible losses, resulting from your access to or use of the app.
                </p>
              </div>

              <div className="mb-12">
                <h2 className="text-2xl font-display font-bold text-foreground mb-4">11. Indemnification</h2>
                <p className="text-muted-foreground leading-relaxed">
                  You agree to indemnify and hold CSW, its affiliates, officers, agents, and employees harmless from any claims, liabilities, damages, losses, and expenses, including reasonable attorney's fees, arising out of or in any way connected with your access to or use of the app, your violation of these Terms, or your infringement of any intellectual property or other rights of any third party.
                </p>
              </div>

              <div className="mb-12">
                <h2 className="text-2xl font-display font-bold text-foreground mb-4">12. Changes to These Terms</h2>
                <p className="text-muted-foreground leading-relaxed">
                  We may update these Terms from time to time to reflect changes in our practices or for other operational, legal, or regulatory reasons. We will notify you of any significant changes by posting the new Terms on the app and updating the "Effective Date" above. Your continued use of the app after any such changes constitutes your acceptance of the new Terms.
                </p>
              </div>

              <div className="mb-12">
                <h2 className="text-2xl font-display font-bold text-foreground mb-4">13. Governing Law</h2>
                <p className="text-muted-foreground leading-relaxed">
                  These Terms are governed by and construed in accordance with the laws of Singapore, without regard to its conflict of law principles. You agree to submit to the exclusive jurisdiction of the courts located in Singapore for the resolution of any disputes.
                </p>
              </div>

              <div className="mb-0">
                <h2 className="text-2xl font-display font-bold text-foreground mb-4">14. Contact Information</h2>
                <p className="text-muted-foreground leading-relaxed mb-4">
                  If you have any questions or concerns about these Terms, please contact us at:
                </p>
                <div className="bg-primary/5 border border-primary/20 rounded-xl p-6">
                  <p className="font-semibold text-foreground mb-2">CSW – Chill Study Work App</p>
                  <a href="mailto:info@csw.live" className="text-primary hover:text-primary/80 transition-colors">
                    info@csw.live
                  </a>
                </div>
              </div>

            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default TermsOfUse;
